import React, { useEffect, useState, useRef } from 'react'
import { usePlanLapse } from '@/hooks/usePlanLapse'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X, Sparkle, PaperPlaneTilt, CircleNotch,
  CaretDown, CaretUp, CheckCircle, Check, ArrowLeft, Trash, Eye, ArrowBendUpLeft, ArrowCounterClockwise, Stop, Microphone,
} from '@phosphor-icons/react'
import { nodeMeta, nodeTone } from './nodeCatalog'
import { Button } from '@/components/ui/Button'
import { Tooltip } from '@/components/ui/Tooltip'
import { inputClass, inputStyle, textareaClass } from '@/lib/styleKit'
import { useScrollLock } from '@/lib/useScrollLock'
import { VoiceRecorder, isSpeechSupported } from './components/VoiceRecorder'
import { workflowsApi } from '@/lib/api'
import { getErrorMessage } from '@/lib/api'
import { describeAction } from '@/lib/workflowActions'
import { toast } from 'sonner'

interface AIWorkflowWizardProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (workflowId: number) => void
}

interface ChatMsg {
  role: 'user' | 'assistant' | 'system'
  content: string
  // A plan attached to an assistant turn. Kept in history so older plans stay
  // visible (superseded) when the user iterates.
  plan?: GeneratedWorkflowPlan
  // On a user turn: the plan name this message was a targeted edit of (reply/quote).
  quotedPlanName?: string
}

interface GeneratedWorkflowNode {
  type: string
  label: string
  description: string
  config: Record<string, any>
}

interface GeneratedWorkflowEdge {
  from_node_index: number
  to_node_index: number
  source_handle?: string
  target_handle?: string
}

interface GeneratedWorkflowPlan {
  workflow_name: string
  workflow_description: string
  nodes: GeneratedWorkflowNode[]
  edges: GeneratedWorkflowEdge[]
}

type WizardPhase = 'chat' | 'review' | 'creating'

// Timeline marker for a planned node. Icon and tone both come from the shared
// catalog, which also owns the unknown-type fallback.
function getNodeVisual(type: string) {
  return { icon: nodeMeta(type).icon, color: nodeTone(type).solid }
}

function getNodeSummary(node: GeneratedWorkflowNode): string {
  const c = node.config || {}
  switch (node.type) {
    case 'trigger': return `Trigger: ${c.trigger_type || 'manual'}`
    case 'sendEmail': return c.subject || 'Email'
    case 'delay': return `Wait ${c.delay_value || '?'} ${c.delay_unit || 'days'}`
    case 'condition': return `If: ${(c.condition_type || 'email_replied').replace(/_/g, ' ')}`
    case 'action': return describeAction({ action_type: c.action_type, action_config: c.action_config })
    case 'enrichContacts': return `Enrich: ${(c.enrich_contacts_config?.enabled_blocks || []).join(', ')}`
    case 'exitWorkflow': return `Exit: ${c.exit_workflow_config?.reason || 'converted'}`
    case 'goal': return `Goal: ${c.goal_config?.goal_type || 'replied'}`
    case 'branchByField': return 'Branch by field'
    case 'aiDecision': return 'AI routes contact'
    case 'createTask': return `Task: ${c.create_task_config?.task_type || 'follow_up'}`
    case 'internalAlert': return `Alert via ${c.internal_alert_config?.channel || 'email'}`
    case 'linkedInAction': return `LinkedIn: ${c.linkedin_action_config?.action_type || 'connect'}`
    case 'crmSync': return `Sync to ${c.crm_sync_config?.provider || 'CRM'}`
    case 'scheduleMeeting': return `Meeting via ${c.schedule_meeting_config?.provider || 'zoom'}`
    default: return node.description || node.type
  }
}

// Render inline markdown emphasis (**bold**) — the AI uses it for the plan name
// and node/connection counts. Users expect rendered emphasis, not raw asterisks.
function renderInline(text: string): React.ReactNode {
  if (!text.includes('**')) return text
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4
      ? <strong key={i} className="font-emphasis">{part.slice(2, -2)}</strong>
      : part
  )
}

const AIWorkflowWizard: React.FC<AIWorkflowWizardProps> = ({ isOpen, onClose, onSuccess }) => {
  const [messages, setMessages] = useState<ChatMsg[]>([
    { role: 'system', content: 'Describe the workflow you want to build. For example: "Outbound sales sequence with 3 emails, enrich contacts first, branch if they reply, exit if converted."' },
  ])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [plan, setPlan] = useState<GeneratedWorkflowPlan | null>(null)
  const { lapsed: planLapsed, reason: lapsedTitle } = usePlanLapse()
  // Index (into messages) of the plan currently treated as active/latest. Other
  // plan-bearing messages render as superseded. null = none active (discarded).
  const [activePlanIndex, setActivePlanIndex] = useState<number | null>(null)
  const [phase, setPhase] = useState<WizardPhase>('chat')
  const [workflowName, setWorkflowName] = useState('')
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set())
  const [editingNode, setEditingNode] = useState<number | null>(null)
  // The plan the user is targeting for a specific edit (chat reply/quote).
  const [replyTo, setReplyTo] = useState<{ index: number; plan: GeneratedWorkflowPlan } | null>(null)
  // Message indices of plans the user has discarded — their cards are removed
  // permanently and can't be viewed again.
  const [discardedPlans, setDiscardedPlans] = useState<Set<number>>(new Set())
  // Message index of the plan currently open in the review pane.
  const [reviewingIndex, setReviewingIndex] = useState<number | null>(null)
  // Discard-on-close confirmation.
  const [showCloseConfirm, setShowCloseConfirm] = useState(false)
  // Assistant message indices showing a skeleton before their content is revealed.
  const [revealing, setRevealing] = useState<Set<number>>(new Set())
  // Voice-to-text recording state.
  const [isRecording, setIsRecording] = useState(false)
  const speechSupported = useRef(isSpeechSupported()).current

  const chatEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  // Aborts the in-flight generation request when the user hits Stop.
  const abortRef = useRef<AbortController | null>(null)

  // Lock the page behind the modal so scrolling inside it doesn't move the
  // blurred background (scrollbar gutter is reserved globally → no layout shift).
  useScrollLock(isOpen)

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Review always opens at the top. Chat keeps its own scroll position across a
  // Back (it stays mounted), so no scroll handling is needed when returning.
  useEffect(() => {
    if (phase === 'review') contentRef.current?.scrollTo({ top: 0 })
  }, [phase])

  // Auto-resize chat textarea between min 36px and max 160px (~7 lines).
  // Only set an inline height when the content actually needs more than the
  // CSS floor, otherwise the inline height creates a tiny content-area /
  // border-box mismatch that triggers an unwanted scrollbar.
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    const next = el.scrollHeight
    if (next > 36) {
      el.style.height = `${Math.min(next, 160)}px`
    } else {
      el.style.height = ''
    }
    // Scrollbar only once content overflows the 160px cap (fully expanded).
    el.style.overflowY = next > 160 ? 'auto' : 'hidden'
  }, [input, phase])

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setMessages([{ role: 'system', content: 'Describe the workflow you want to build. For example: "Outbound sales sequence with 3 emails, enrich contacts first, branch if they reply, exit if converted."' }])
      setInput('')
      setPlan(null)
      setActivePlanIndex(null)
      setPhase('chat')
      setWorkflowName('')
      setExpandedNodes(new Set())
      setEditingNode(null)
      setReplyTo(null)
      setDiscardedPlans(new Set())
      setReviewingIndex(null)
      setShowCloseConfirm(false)
      setIsRecording(false)
      setRevealing(new Set())
    }
  }, [isOpen])

  const handleSend = async () => {
    if (!input.trim() || isLoading) return

    // If the user is targeting a specific plan, send it so the backend edits
    // that plan in place; tag the visible message with the quoted plan name.
    const target = replyTo?.plan ?? null
    const userMsg: ChatMsg = target
      ? { role: 'user', content: input, quotedPlanName: target.workflow_name }
      : { role: 'user', content: input }
    const updatedMessages = [...messages, userMsg]
    setMessages(updatedMessages)
    setInput('')
    setReplyTo(null)
    setIsLoading(true)
    // Keep the composer focused so the user can keep typing while it generates.
    setTimeout(() => textareaRef.current?.focus(), 0)

    try {
      const apiMessages = updatedMessages
        .filter(m => m.role !== 'system')
        .map(m => ({ role: m.role, content: m.content }))

      const controller = new AbortController()
      abortRef.current = controller
      const res = await workflowsApi.aiGenerateChat(
        target ? { messages: apiMessages, target_plan: target } : { messages: apiMessages },
        { signal: controller.signal },
      )
      const { message, phase: responsePhase, plan: generatedPlan } = res.data

      const isPlan = responsePhase === 'plan_ready' && !!generatedPlan
      const assistantMsg: ChatMsg = isPlan
        ? { role: 'assistant', content: message, plan: generatedPlan }
        : { role: 'assistant', content: message }
      const finalMessages = [...updatedMessages, assistantMsg]
      setMessages(finalMessages)

      // Show a skeleton sized to the actual reply before revealing it.
      const revealIdx = finalMessages.length - 1
      setRevealing(prev => new Set(prev).add(revealIdx))
      setTimeout(() => {
        setRevealing(prev => {
          const next = new Set(prev)
          next.delete(revealIdx)
          return next
        })
        // Revealing the message grows the content (and shows the plan card) but
        // doesn't change `messages`, so scroll to the end here to keep it in view.
        requestAnimationFrame(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }))
      }, 3000)

      if (isPlan && generatedPlan) {
        // New plan becomes the active/latest; earlier plan cards stay as history.
        // Stay in chat and surface a "Review workflow plan" card instead of
        // auto-switching (the old auto-switch flashed then jumped mid-scroll).
        setPlan(generatedPlan)
        setActivePlanIndex(finalMessages.length - 1)
        setWorkflowName(generatedPlan.workflow_name)
        // Expand sendEmail nodes by default
        const emailIndices = new Set(
          generatedPlan.nodes.map((n, i) => n.type === 'sendEmail' ? i : -1).filter(i => i >= 0)
        )
        setExpandedNodes(emailIndices)
      }
    } catch (error: any) {
      // User pressed Stop — acknowledge the interruption, don't show an error.
      const aborted = error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED'
      if (aborted) {
        setMessages(prev => [
          ...prev,
          { role: 'system', content: 'You stopped this response before it finished. Send a message to pick up where you left off.' },
        ])
      } else {
        toast.error(getErrorMessage(error, 'Failed to generate response'))
      }
    } finally {
      abortRef.current = null
      setIsLoading(false)
      // Re-focus the composer (it was disabled during loading) so the cursor is
      // ready for the next message right after a response.
      setTimeout(() => textareaRef.current?.focus(), 0)
    }
  }

  // Stop an in-flight generation. The aborted request is caught in handleSend,
  // which posts an interruption note.
  const handleStop = () => {
    abortRef.current?.abort()
  }

  // Voice transcript confirmed (tick or 30s auto-stop): append to the composer
  // text so the user can review/edit before sending. Cancel just restores the bar.
  const handleVoiceComplete = (transcript: string) => {
    setIsRecording(false)
    if (transcript) {
      setInput(prev => (prev.trim() ? `${prev.trim()} ${transcript}` : transcript))
    }
    // Focus and place the caret at the end (textarea remounts → caret defaults to start).
    setTimeout(() => {
      const el = textareaRef.current
      if (!el) return
      el.focus()
      const len = el.value.length
      el.setSelectionRange(len, len)
    }, 0)
  }

  // Permanently discard the plan at message index `idx`: its card is removed and
  // can't be viewed again. Only that plan is affected. If it was the latest, the
  // newest surviving plan is promoted to latest.
  const handleDiscard = (idx: number) => {
    const nextDiscarded = new Set(discardedPlans)
    nextDiscarded.add(idx)
    setDiscardedPlans(nextDiscarded)

    if (replyTo?.index === idx) setReplyTo(null)

    if (activePlanIndex === idx) {
      // Discarding the latest does NOT promote an older version — there is simply
      // no active/latest plan until the user generates or re-selects one.
      setActivePlanIndex(null)
      setPlan(null)
      setWorkflowName('')
      setExpandedNodes(new Set())
      setEditingNode(null)
      if (phase === 'review') setPhase('chat')
    }
  }

  // Bring a discarded plan back (undo). It returns as a normal historical card.
  const handleRestore = (idx: number) => {
    setDiscardedPlans(prev => {
      const next = new Set(prev)
      next.delete(idx)
      return next
    })
  }

  // The user has unsaved work if they've chatted, generated a plan, or typed.
  const isDirty =
    plan !== null ||
    input.trim().length > 0 ||
    messages.some(m => m.role === 'user' || m.role === 'assistant')

  // Closing the wizard (X or backdrop) confirms first when there's work to lose.
  const requestClose = () => {
    if (isDirty) setShowCloseConfirm(true)
    else onClose()
  }

  // Open the plan at message index `idx` in review. Viewing does NOT change which
  // plan is "Latest" (that only changes on a new generation or discard) — so a
  // previous plan stays previous after you peek at it.
  const reviewPlanAt = (idx: number) => {
    const p = messages[idx]?.plan
    if (!p) return
    setPlan(p)
    setReviewingIndex(idx)
    setWorkflowName(p.workflow_name)
    setExpandedNodes(new Set(p.nodes.map((n, i) => (n.type === 'sendEmail' ? i : -1)).filter(i => i >= 0)))
    setEditingNode(null)
    setPhase('review')
  }

  // Target a plan for a specific edit: quote it above the composer and focus.
  const handleReplyTo = (idx: number) => {
    const p = messages[idx]?.plan
    if (!p) return
    setReplyTo({ index: idx, plan: p })
    setPhase('chat')
    setTimeout(() => textareaRef.current?.focus(), 0)
  }

  const handleCreate = async () => {
    if (!plan) return

    setPhase('creating')
    try {
      const res = await workflowsApi.aiGenerateCreate({
        plan,
        workflow_name: workflowName || undefined,
        // Persist the generation transcript so the in-canvas chat can continue it.
        messages: messages
          .filter(m => m.role === 'user' || m.role === 'assistant')
          .map(m => ({ role: m.role, content: m.content })),
      })
      onSuccess(res.data.workflow_id)
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create workflow'))
      setPhase('review')
    }
  }

  const toggleNodeExpand = (idx: number) => {
    setExpandedNodes(prev => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const updateNodeConfig = (idx: number, field: string, value: string) => {
    if (!plan) return
    setPlan({
      ...plan,
      nodes: plan.nodes.map((n, i) =>
        i === idx ? { ...n, config: { ...n.config, [field]: value } } : n
      ),
    })
  }

  // A plan surfaced inline in the chat. `isLatest` → full brand card with
  // primary actions; otherwise a compact, dimmed "Previous" row (Von Restorff +
  // recency hierarchy). `version` is the generation ordinal (v1, v2…) so plans
  // that share the same name are still distinguishable.
  const renderPlanCard = (p: GeneratedWorkflowPlan, idx: number, isLatest: boolean, version: number, isDiscarded: boolean) => {
    if (isDiscarded) {
      // Tombstone — greyed, struck-through, no actions except Restore (undo).
      return (
        <div className="flex justify-start">
          <div className="max-w-[80%] w-full rounded-xl border border-border/40 bg-muted/20 px-3.5 py-2.5 opacity-50 hover:opacity-90 transition-opacity duration-150 dark:border-white/[0.05] dark:bg-white/[0.015]">
            <div className="flex items-center gap-2.5">
              <div className="h-6 w-6 rounded-md bg-muted dark:bg-white/[0.04] flex items-center justify-center flex-shrink-0">
                <Sparkle className="h-3 w-3 text-muted-foreground dark:text-[#62666d]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="min-w-0 truncate text-[13px] font-medium line-through text-muted-foreground dark:text-[#62666d]">
                    {p.workflow_name}
                  </span>
                  <span className="flex-shrink-0 text-[10px] font-emphasis tabular-nums text-muted-foreground/60 dark:text-[#62666d]">
                    v{version}
                  </span>
                  <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-emphasis uppercase tracking-[0.06em] bg-muted/60 text-muted-foreground dark:bg-white/[0.04] dark:text-[#62666d]">
                    Discarded
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground/60 dark:text-[#62666d] tabular-nums">
                  {p.nodes.length} nodes · {p.edges.length} connections
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleRestore(idx)}
                title="Restore this plan"
                aria-label="Restore this plan"
                className="flex-shrink-0 inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[12px] font-[510] text-muted-foreground hover:text-[#5e6ad2] hover:bg-[#5e6ad2]/[0.08] dark:text-[#8a8f98] dark:hover:text-[#a5b4fc] dark:hover:bg-[#5e6ad2]/[0.12] transition-colors duration-150"
              >
                <ArrowCounterClockwise className="h-3.5 w-3.5" />
                Restore
              </button>
            </div>
          </div>
        </div>
      )
    }
    if (isLatest) {
      return (
        <div className="flex justify-start">
          <div className="max-w-[80%] w-full rounded-2xl rounded-bl-md border border-border/60 bg-muted/40 dark:border-white/[0.08] dark:bg-white/[0.03] p-4">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.16] flex items-center justify-center flex-shrink-0">
                <Sparkle className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
              </div>
              <span className="min-w-0 truncate text-sm font-emphasis text-foreground dark:text-[#f7f8f8]">
                {p.workflow_name}
              </span>
              <span className="ml-auto flex-shrink-0 text-[10px] font-emphasis tabular-nums text-muted-foreground dark:text-[#62666d]">
                v{version}
              </span>
              <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-emphasis uppercase tracking-[0.06em] bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/[0.14] dark:text-emerald-300">
                Latest
              </span>
              <Tooltip label="Reply to make a targeted change">
                <button
                  type="button"
                  onClick={() => handleReplyTo(idx)}
                  aria-label="Reply to make a targeted change"
                  className="flex-shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-[#5e6ad2] hover:bg-[#5e6ad2]/[0.08] dark:text-[#8a8f98] dark:hover:text-[#a5b4fc] dark:hover:bg-[#5e6ad2]/[0.12] transition-colors duration-150"
                >
                  <ArrowBendUpLeft className="h-3.5 w-3.5" />
                </button>
              </Tooltip>
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground dark:text-[#8a8f98] tabular-nums">
              {p.nodes.length} nodes · {p.edges.length} connections
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => reviewPlanAt(idx)}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-[#5e6ad2] text-white hover:bg-[#7170ff] transition-colors duration-150"
                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
              >
                <Eye className="h-4 w-4" />
                Review workflow plan
              </button>
              <button
                type="button"
                onClick={() => handleDiscard(idx)}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:text-[#8a8f98] dark:hover:text-red-400 dark:hover:bg-red-500/[0.08] transition-colors duration-150"
                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
              >
                <Trash className="h-3.5 w-3.5" />
                Discard plan
              </button>
            </div>
          </div>
        </div>
      )
    }
    // Superseded — compact, dimmed, secondary actions.
    return (
      <div className="flex justify-start">
        <div className="max-w-[80%] w-full rounded-xl border border-border/50 bg-muted/30 px-3.5 py-2.5 opacity-70 hover:opacity-100 transition-opacity duration-150 dark:border-white/[0.06] dark:bg-white/[0.02]">
          <div className="flex items-center gap-2.5">
            <div className="h-6 w-6 rounded-md bg-muted dark:bg-white/[0.05] flex items-center justify-center flex-shrink-0">
              <Sparkle className="h-3 w-3 text-muted-foreground dark:text-[#62666d]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="min-w-0 truncate text-[13px] font-medium text-muted-foreground dark:text-[#8a8f98]">
                  {p.workflow_name}
                </span>
                <span className="flex-shrink-0 text-[10px] font-emphasis tabular-nums text-muted-foreground/70 dark:text-[#62666d]">
                  v{version}
                </span>
                <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-emphasis uppercase tracking-[0.06em] bg-muted/70 text-muted-foreground dark:bg-white/[0.05] dark:text-[#62666d]">
                  Previous
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground/80 dark:text-[#62666d] tabular-nums">
                {p.nodes.length} nodes · {p.edges.length} connections
              </p>
            </div>
            <div className="flex-shrink-0 flex items-center gap-0.5">
              <Tooltip label="Reply to make a targeted change">
                <button
                  type="button"
                  onClick={() => handleReplyTo(idx)}
                  aria-label="Reply to make a targeted change"
                  className="inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-[#5e6ad2] hover:bg-[#5e6ad2]/[0.08] dark:text-[#8a8f98] dark:hover:text-[#a5b4fc] dark:hover:bg-[#5e6ad2]/[0.12] transition-colors duration-150"
                >
                  <ArrowBendUpLeft className="h-3.5 w-3.5" />
                </button>
              </Tooltip>
              <button
                type="button"
                onClick={() => reviewPlanAt(idx)}
                className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[12px] font-[510] text-muted-foreground hover:text-[#5e6ad2] hover:bg-[#5e6ad2]/[0.08] dark:text-[#8a8f98] dark:hover:text-[#a5b4fc] dark:hover:bg-[#5e6ad2]/[0.12] transition-colors duration-150"
              >
                <Eye className="h-3.5 w-3.5" />
                View
              </button>
              <Tooltip label="Discard this plan">
                <button
                  type="button"
                  onClick={() => handleDiscard(idx)}
                  aria-label="Discard this plan"
                  className="inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:text-[#8a8f98] dark:hover:text-red-400 dark:hover:bg-red-500/[0.08] transition-colors duration-150"
                >
                  <Trash className="h-3.5 w-3.5" />
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!isOpen) return null

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={requestClose}
      >
        <motion.div
          className="relative bg-card border border-border/60 rounded-2xl shadow-2xl w-full max-w-2xl h-[85vh] flex flex-col overflow-hidden dark:bg-[#191a1b] dark:border-white/[0.08]"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border/40 dark:border-white/[0.06]">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.16] flex items-center justify-center">
                <Sparkle className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
              </div>
              <div>
                <h2 className="text-base font-emphasis text-foreground dark:text-[#f7f8f8]">AI Workflow Generator</h2>
                <p className="text-xs text-muted-foreground dark:text-[#8a8f98]">
                  {phase === 'chat' && 'Describe your workflow'}
                  {phase === 'review' && 'Review your workflow plan'}
                  {phase === 'creating' && 'Creating workflow...'}
                </p>
              </div>
            </div>
            <button onClick={requestClose} aria-label="Close" className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted/70 hover:text-foreground dark:text-[#8a8f98] dark:hover:bg-white/[0.06] dark:hover:text-[#f7f8f8] transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div ref={contentRef} className="flex-1 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]">
            {/* PHASE: Chat — kept mounted (toggle hidden) so scroll position
                survives a Back from review without a remount flicker. */}
            <div className={`flex flex-col h-full min-h-[400px] ${phase === 'chat' ? '' : 'hidden'}`}>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <div className="min-h-full flex flex-col justify-end space-y-3">
                  {messages.map((msg, i) => {
                    const showSkeleton = msg.role === 'assistant' && revealing.has(i)
                    return (
                    <React.Fragment key={i}>
                      {showSkeleton ? (
                        <div className="flex justify-start">
                          {/* Skeleton mirrors the real reply word-for-word (same bubble,
                              wrapping and size) so the content swaps in with no shift. */}
                          <div className="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-bl-md text-sm leading-relaxed bg-muted/70 dark:bg-white/[0.06]">
                            {msg.content.split('\n').map((line, j) => (
                              <p key={j} className={j > 0 ? 'mt-1.5' : ''}>
                                <span className="box-decoration-clone rounded bg-foreground/10 text-transparent animate-pulse select-none dark:bg-white/10">
                                  {line}
                                </span>
                              </p>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                              msg.role === 'user'
                                ? 'bg-[#5e6ad2] text-white rounded-br-md'
                                : msg.role === 'system'
                                  ? 'bg-muted/50 text-muted-foreground rounded-bl-md italic dark:bg-white/[0.04] dark:text-[#8a8f98]'
                                  : 'bg-muted/70 text-foreground rounded-bl-md dark:bg-white/[0.06] dark:text-[#f7f8f8]'
                            }`}
                          >
                            {msg.quotedPlanName && (
                              <div className="mb-1.5 flex items-center gap-1 border-b border-white/25 pb-1 text-[11px] text-white/85">
                                <ArrowBendUpLeft className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{msg.quotedPlanName}</span>
                              </div>
                            )}
                            {msg.content.split('\n').map((line, j) => (
                              <p key={j} className={j > 0 ? 'mt-1.5' : ''}>
                                {msg.role === 'assistant' ? renderInline(line) : line}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                      {msg.plan && !revealing.has(i) && (
                        <div id={`plan-msg-${i}`}>
                          {renderPlanCard(
                            msg.plan,
                            i,
                            i === activePlanIndex,
                            messages.slice(0, i + 1).filter(m => m.plan).length,
                            discardedPlans.has(i),
                          )}
                        </div>
                      )}
                    </React.Fragment>
                    )
                  })}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="inline-flex items-center justify-center bg-muted/70 dark:bg-white/[0.06] rounded-2xl rounded-bl-md px-3 py-2">
                        <motion.span
                          animate={{ scale: [1, 1.25, 1], opacity: [0.6, 1, 0.6] }}
                          transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
                          className="inline-flex items-center justify-center"
                          style={{ transformOrigin: 'center' }}
                        >
                          <Sparkle className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                        </motion.span>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                  </div>
                </div>
            </div>

            {/* PHASE: Review */}
            {phase === 'review' && plan && (
              <div className="px-6 py-4 space-y-3">
                {/* Workflow header */}
                <div className="p-3 rounded-xl border border-border/50 bg-muted/40 dark:bg-white/[0.03] dark:border-white/[0.08]">
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] font-emphasis text-muted-foreground dark:text-[#8a8f98] uppercase tracking-[0.06em] shrink-0">Name:</label>
                    <input
                      type="text"
                      value={workflowName}
                      onChange={(e) => setWorkflowName(e.target.value)}
                      className="flex-1 bg-transparent border-none text-sm font-emphasis text-foreground dark:text-[#f7f8f8] focus:outline-none focus:ring-0 p-0"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground dark:text-[#8a8f98] mt-1">{plan.workflow_description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-emphasis tabular-nums bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]">
                      {plan.nodes.length} nodes
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-emphasis tabular-nums bg-muted/60 text-muted-foreground dark:bg-white/[0.05] dark:text-[#8a8f98]">
                      {plan.edges.length} connections
                    </span>
                  </div>
                </div>

                {/* Node timeline */}
                <div className="relative pl-6">
                  <div className="absolute left-[20.5px] top-3 bottom-3 w-px bg-border/60 dark:bg-white/[0.08]" />

                  {plan.nodes.map((node, idx) => {
                    const { icon: Icon, color } = getNodeVisual(node.type)
                    const isExpanded = expandedNodes.has(idx)
                    const isEditing = editingNode === idx
                    const isEmail = node.type === 'sendEmail'
                    const summary = getNodeSummary(node)

                    return (
                      <div key={idx} className="relative py-1.5">
                        <div className={`absolute left-[-13px] h-5 w-5 rounded-full ${color} flex items-center justify-center z-10`}>
                          <Icon className="h-2.5 w-2.5 text-white" />
                        </div>

                        {isEmail ? (
                          /* Expandable email card */
                          <div className="ml-4 rounded-lg border border-border/50 overflow-hidden transition-colors hover:border-border/80 dark:border-white/[0.08] dark:bg-white/[0.02] dark:hover:border-white/[0.14]">
                            <button
                              type="button"
                              onClick={() => toggleNodeExpand(idx)}
                              className="w-full flex items-center justify-between p-3 text-left"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-emphasis text-foreground dark:text-[#f7f8f8]">{node.label}</span>
                                </div>
                                <p className="text-xs text-muted-foreground dark:text-[#8a8f98] mt-0.5 truncate">{node.config.subject || summary}</p>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setEditingNode(isEditing ? null : idx); setExpandedNodes(prev => new Set(prev).add(idx)) }}
                                  className={
                                    'inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[12px] font-[510] border transition-colors duration-150 ' +
                                    (isEditing
                                      ? 'border-[#5e6ad2]/40 bg-[#5e6ad2]/[0.08] text-[#5e6ad2] dark:border-[#5e6ad2]/40 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]'
                                      : 'border-border/50 text-muted-foreground hover:text-[#5e6ad2] hover:border-[#5e6ad2]/40 hover:bg-[#5e6ad2]/[0.06] dark:border-white/[0.08] dark:text-[#8a8f98] dark:hover:text-[#a5b4fc] dark:hover:border-[#5e6ad2]/40 dark:hover:bg-[#5e6ad2]/[0.12]')
                                  }
                                  title={isEditing ? 'Done editing' : 'Edit email'}
                                  aria-label={isEditing ? 'Done editing' : 'Edit email'}
                                >
                                  {isEditing && <Check className="h-3 w-3" weight="bold" />}
                                  {isEditing ? 'Done' : 'Edit'}
                                </button>
                                <span className="text-muted-foreground dark:text-[#8a8f98]">
                                  {isExpanded ? <CaretUp className="h-3.5 w-3.5" /> : <CaretDown className="h-3.5 w-3.5" />}
                                </span>
                              </div>
                            </button>
                            {isExpanded && (
                              <div className="px-3 pb-3 border-t border-border/30 dark:border-white/[0.06]">
                                {isEditing ? (
                                  <div className="space-y-3 mt-3">
                                    <div>
                                      <label className="block mb-1 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]">Subject</label>
                                      <input
                                        type="text"
                                        value={node.config.subject || ''}
                                        onChange={(e) => updateNodeConfig(idx, 'subject', e.target.value)}
                                        className={inputClass}
                                        style={inputStyle}
                                      />
                                    </div>
                                    <div>
                                      <label className="block mb-1 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]">Body</label>
                                      <textarea
                                        value={node.config.body || ''}
                                        onChange={(e) => updateNodeConfig(idx, 'body', e.target.value)}
                                        rows={5}
                                        className={textareaClass}
                                        style={inputStyle}
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="mt-3 space-y-1.5">
                                    <p className="text-xs font-medium text-foreground dark:text-[#f7f8f8]">
                                      Subject: <span className="font-normal text-muted-foreground dark:text-[#8a8f98]">{node.config.subject || '—'}</span>
                                    </p>
                                    {node.config.body ? (
                                      <div className="text-xs text-muted-foreground dark:text-[#8a8f98] whitespace-pre-wrap leading-relaxed rounded-lg bg-muted/40 dark:bg-white/[0.03] p-2.5">
                                        {node.config.body}
                                      </div>
                                    ) : (
                                      <p className="flex items-center gap-1.5 text-xs italic text-muted-foreground/70 dark:text-[#62666d]">
                                        <Sparkle className="h-3 w-3 flex-shrink-0" weight="fill" />
                                        Body is written by AI when this email is sent.
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          /* Simple inline node */
                          <div className="ml-4 flex items-center gap-2 py-0.5">
                            <span className="text-xs font-medium text-foreground dark:text-[#f7f8f8]">{node.label}</span>
                            <span className="text-xs text-muted-foreground dark:text-[#8a8f98]">{summary !== node.label ? `- ${summary}` : ''}</span>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* PHASE: Creating */}
            {phase === 'creating' && (
              <div className="flex flex-col items-center justify-center py-16">
                <CircleNotch className="h-8 w-8 animate-spin text-[#5e6ad2] dark:text-[#a5b4fc] mb-4" weight="bold" />
                <p className="text-sm text-muted-foreground dark:text-[#8a8f98]">Creating workflow…</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-border/40 dark:border-white/[0.06]">
            {phase === 'chat' && (
              <div className="space-y-2">
                {replyTo && (
                  <div
                    role="button"
                    tabIndex={0}
                    title="Jump to this plan"
                    onClick={() => document.getElementById(`plan-msg-${replyTo.index}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                    className="relative flex items-start gap-2 cursor-pointer rounded-lg border border-[#5e6ad2]/25 bg-[#5e6ad2]/[0.06] px-3 py-2 pr-10 transition-colors hover:bg-[#5e6ad2]/[0.1] dark:border-[#5e6ad2]/30 dark:bg-[#5e6ad2]/[0.10] dark:hover:bg-[#5e6ad2]/[0.16]"
                  >
                    <ArrowBendUpLeft className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-emphasis uppercase tracking-[0.06em] text-[#5e6ad2] dark:text-[#a5b4fc]">Editing plan</span>
                        <span className="px-1.5 py-px rounded-full text-[9px] font-emphasis tabular-nums bg-[#5e6ad2]/15 text-[#5e6ad2] dark:bg-[#5e6ad2]/25 dark:text-[#a5b4fc]">
                          v{messages.slice(0, replyTo.index + 1).filter(m => m.plan).length}
                        </span>
                      </div>
                      <div className="truncate text-[12px] font-medium text-foreground dark:text-[#f7f8f8]">{replyTo.plan.workflow_name}</div>
                      <div className="text-[10px] tabular-nums text-muted-foreground dark:text-[#8a8f98]">
                        {replyTo.plan.nodes.length} nodes · {replyTo.plan.edges.length} connections
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setReplyTo(null) }}
                      aria-label="Cancel targeted edit"
                      className="absolute top-2 right-2 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-[#5e6ad2]/10 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                {isRecording ? (
                  <VoiceRecorder onComplete={handleVoiceComplete} onCancel={() => setIsRecording(false)} />
                ) : (
                <>
                <div className="flex-1">
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter sends, Shift+Enter inserts newline
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleSend()
                      }
                    }}
                    placeholder={replyTo ? 'Describe the change to this plan…' : 'Describe your workflow…'}
                    autoFocus
                    rows={1}
                    aria-label="Workflow description"
                    className="block w-full px-3 rounded-md border transition-colors duration-150 resize-none overflow-y-hidden max-h-[160px] bg-background border-border/60 text-foreground placeholder:text-muted-foreground/50 hover:bg-background hover:border-border/80 focus:outline-none focus-visible:border-[#7170ff]/55 dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:placeholder:text-[#62666d] dark:hover:bg-white/[0.03] dark:hover:border-white/[0.14] dark:focus-visible:border-[#7170ff]/75 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      fontSize: '13px',
                      fontWeight: 510,
                      letterSpacing: '-0.13px',
                      lineHeight: '20px',
                      // Explicit inline guarantees parity with the 36px Send button height.
                      minHeight: 36,
                      paddingTop: 7,
                      paddingBottom: 7,
                    }}
                  />
                </div>
                {speechSupported && !isLoading && (
                  <span className="relative group flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setIsRecording(true)}
                      aria-label="Record voice"
                      className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-border/60 text-muted-foreground hover:bg-muted/50 hover:text-[#5e6ad2] dark:border-white/[0.08] dark:text-[#8a8f98] dark:hover:bg-white/[0.04] dark:hover:text-[#a5b4fc] transition-colors duration-150"
                    >
                      <Microphone className="h-4 w-4" />
                    </button>
                    <span
                      role="tooltip"
                      className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-md px-2.5 py-1.5 text-[11px] font-medium opacity-0 shadow-lg transition-all duration-150 group-hover:translate-y-0 group-hover:opacity-100 bg-[#191a1b] text-white dark:bg-[#e8e9ec] dark:text-[#0f1011]"
                    >
                      Tap to record
                      <span className="absolute left-1/2 top-full -mt-1 h-2 w-2 -translate-x-1/2 rotate-45 bg-[#191a1b] dark:bg-[#e8e9ec]" />
                    </span>
                  </span>
                )}
                {isLoading ? (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleStop}
                    icon={<Stop className="h-4 w-4" weight="fill" />}
                    className="h-9 flex-shrink-0"
                  >
                    Stop
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSend}
                    disabled={!input.trim() || planLapsed}
                    title={planLapsed ? lapsedTitle : undefined}
                    icon={<PaperPlaneTilt className="h-4 w-4" />}
                    className="h-9 flex-shrink-0"
                  >
                    Send
                  </Button>
                )}
                </>
                )}
                </div>
              </div>
            )}

            {phase === 'review' && (
              <div className="flex items-center justify-between gap-2">
                <Button variant="ghost" onClick={() => setPhase('chat')} icon={<ArrowLeft className="h-4 w-4" />}>
                  Back to chat
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      if (reviewingIndex != null) handleDiscard(reviewingIndex)
                      setPhase('chat')
                    }}
                    icon={<Trash className="h-4 w-4" />}
                    className="text-muted-foreground hover:bg-red-50 hover:text-red-600 dark:text-[#8a8f98] dark:hover:bg-red-500/[0.08] dark:hover:text-red-400"
                  >
                    Discard
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleCreate}
                    disabled={!workflowName.trim()}
                    icon={<CheckCircle className="h-4 w-4" />}
                  >
                    Create & open in Builder
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Inline discard confirmation — contained in the card (no portal /
              scroll-lock / second full-screen blur) so dismissing it doesn't
              shift the layout or flicker the modal. */}
          {showCloseConfirm && (
            <div
              className="absolute inset-0 z-20 flex items-center justify-center p-6 bg-background/70 backdrop-blur-sm dark:bg-[#0f1011]/70"
              onClick={() => setShowCloseConfirm(false)}
            >
              <div
                role="alertdialog"
                aria-modal="true"
                onClick={e => e.stopPropagation()}
                className="w-full max-w-sm rounded-2xl border border-border/60 bg-card p-6 shadow-2xl dark:border-white/[0.08] dark:bg-[#191a1b]"
              >
                <h3 className="text-base font-emphasis tracking-tight text-foreground dark:text-[#f7f8f8]">
                  Discard this workflow?
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground dark:text-[#d0d6e0]">
                  You'll lose this conversation and the generated plan(s). This can't be undone.
                </p>
                <div className="mt-6 flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowCloseConfirm(false)}>
                    Back to workflow
                  </Button>
                  <Button variant="destructive" onClick={() => { setShowCloseConfirm(false); onClose() }}>
                    Discard
                  </Button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  )
}

export default AIWorkflowWizard
